import { useState } from "react";
import { Select, selectState as ss } from "@bfc-software/pallet";
import type { SelectMsg, SelectState } from "@bfc-software/pallet";
import { match } from "ts-pattern";
import { useCurrentLocalization } from "@bfc-software/bfc-web-ui/i18n";

import { useTaskFilters, zonesParam } from "../-data/filters";
import { useZoneCatalog, type ZoneOption } from "../-data/use-zone-catalog";

/**
 * The zone filter, shared by both tabs.
 *
 * It sits in the tab bar rather than inside either tab, for the same reason
 * `SelectorFilter` beside it does (see its docblock): `zones` is a URL param
 * `useTaskFilters` owns, and a control that lived inside one tab would stop
 * narrowing the moment the other tab was open.
 *
 * A value field (picked names, a `(N)` count, an X) that opens a popover with a
 * search box and a multi-select list. A wave is often in two zones at once, so
 * this holds several picks where `SelectorFilter` holds one. That is pallet's
 * `Select` in its `multiple` arm; the search lives in the popover, not in the
 * field.
 *
 * Unlike `SelectorFilter`, this reads no `AuthContext` - `/zones/all` takes no
 * org or warehouse param, so there is nothing to plumb in.
 *
 * Selection is the URL's (`filters.zone`); the typed text is the only local
 * state (`useState`), since it is not a filter in its own right, only the means
 * of picking one, and not something a manager would want to send to someone
 * else. Pallet owns whether the list is open and reports it; this filter only
 * clears its text on `closed`. Pallet never filters, so the options are
 * narrowed here, on the label only: no `zone-options.ts` module next door to
 * `selector-options.ts`, because a zone's id is an item-service BIGINT nobody
 * has ever seen, let alone would type.
 */
/** Pure: everything the dropdown shows, from the catalog and the picks. */
function zoneSelectState(a: {
  options: ReadonlyArray<ZoneOption>;
  isPending: boolean;
  selected: Array<ZoneOption>;
  text: string;
  t: (key: string) => string;
}): SelectState<ZoneOption> {
  const options = a.isPending
    ? ss.loading(a.t("tasks.zoneFilter.loading"))
    : ss.ready({
        items: a.options.filter(ss.byLabel(a.text)),
        emptyLabel: a.t("tasks.zoneFilter.empty"),
      });
  return ss.withPicks(
    ss.withSearch(
      ss.multiple({
        label: ss.ariaLabel(a.t("tasks.zoneFilter.ariaLabel")),
        placeholder: a.t("tasks.zoneFilter.placeholder"),
        options,
      }),
      {
        placeholder: a.t("tasks.zoneFilter.searchPlaceholder"),
        text: a.text,
      },
    ),
    ss.subsetSelection(a.selected),
  );
}

export function ZoneFilter() {
  const [t] = useCurrentLocalization();
  const { filters, setSearch } = useTaskFilters();
  const catalog = useZoneCatalog();
  const options = catalog.data ?? [];

  /** The only local state: the search box. The picks live in the URL. */
  const [text, setText] = useState("");

  /**
   * Off the ADT rather than off the catalog: a link carrying an ext id the
   * catalog does not have (a deleted zone, a paste from another warehouse)
   * still names itself instead of vanishing from the field.
   */
  const selected: ZoneOption[] =
    filters.zone.kind === "any"
      ? []
      : filters.zone.extIds.map(
          (extId) =>
            options.find((option) => option.id === extId) ?? {
              id: extId,
              label: extId,
            },
        );

  const commit = (extIds: string[]) =>
    setSearch({ zones: zonesParam({ kind: "some", extIds }) });

  // `all` resolves against the FULL catalog, not the searched-down list.
  const idsOf = (selection: ss.MultipleSelection<ZoneOption>) =>
    match(selection)
      .with({ kind: "all" }, () => options.map((option) => option.id))
      .with({ kind: "subset" }, (s) => s.items.map((option) => option.id))
      .with({ kind: "empty" }, () => [])
      .exhaustive();

  const onMsg = (msg: SelectMsg<ZoneOption>) =>
    match(msg)
      .with({ kind: "searched" }, ({ text }) => setText(text))
      // Reset the search box, so reopening shows the full list.
      .with({ kind: "closed" }, () => setText(""))
      .with({ kind: "pickedN" }, ({ selection }) => commit(idsOf(selection)))
      .with({ kind: "cleared" }, () => commit([]))
      // `picked` cannot arrive from `multiple`
      .with({ kind: "picked" }, { kind: "opened" }, () => {})
      .exhaustive();

  return (
    <Select
      state={zoneSelectState({
        options,
        isPending: catalog.isPending,
        selected,
        text,
        t,
      })}
      onMsg={onMsg}
      className="flex-1 min-w-0"
      data-testid="zone-filter"
    />
  );
}
